/**
 * InstallPostpartnerSheet - Platform-aware PWA install instructions
 * Uses BottomSheet pattern with tailored guidance for each platform/browser
 */

import { memo, useCallback, useState } from 'react';
import { BottomSheet } from './BottomSheet';
import { Download, Share2, Check, Copy, Link, Smartphone, MoreVertical } from '@/lib/icons';
import { triggerHaptic } from '@/lib/haptics';
import type { PWAInstallState, InstallMethod } from '@/hooks/usePWAInstall';

interface InstallPostpartnerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  installState: PWAInstallState;
}

/** Step component for manual instructions */
const InstructionStep = memo(function InstructionStep({
  step,
  icon: Icon,
  text,
  subtext,
}: {
  step: number;
  icon: React.ElementType;
  text: string;
  subtext?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
        <span className="text-label font-semibold text-primary-700">{step}</span>
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2">
          <Icon className="w-[18px] h-[18px] text-gray-500 flex-shrink-0" />
          <span className="text-body text-gray-900">{text}</span>
        </div>
        {subtext && <p className="text-label text-gray-500 mt-0.5 ml-[26px]">{subtext}</p>}
      </div>
    </div>
  );
});

/** Native prompt content (Android Chrome/Edge/Samsung) */
const NativePromptContent = memo(function NativePromptContent({
  onInstall,
  isInstalling,
}: {
  onInstall: () => void;
  isInstalling: boolean;
}) {
  return (
    <div className="space-y-4 pb-4">
      <p className="text-body text-gray-600">
        Install Postpartner for quick access from your home screen with a faster, app-like experience.
      </p>
      <button
        onClick={onInstall}
        disabled={isInstalling}
        className="w-full py-3.5 px-4 bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                   text-white font-semibold rounded-button motion-safe:transition-colors
                   disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isInstalling ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Installing...
          </>
        ) : (
          <>
            <Download className="w-5 h-5" />
            Install Postpartner
          </>
        )}
      </button>
    </div>
  );
});

/** iOS Safari instructions */
const IOSSafariContent = memo(function IOSSafariContent() {
  return (
    <div className="divide-y divide-gray-100 pb-2">
      <InstructionStep
        step={1}
        icon={Share2}
        text="Tap the Share button"
        subtext="At the bottom of Safari"
      />
      <InstructionStep
        step={2}
        icon={Smartphone}
        text='Tap "Add to Home Screen"'
        subtext="Scroll down if needed"
      />
    </div>
  );
});

/** iOS Chrome/Edge instructions */
const IOSChromeContent = memo(function IOSChromeContent() {
  return (
    <div className="divide-y divide-gray-100 pb-2">
      <InstructionStep
        step={1}
        icon={Share2}
        text="Tap the Share button"
        subtext="In the address bar"
      />
      <InstructionStep
        step={2}
        icon={Smartphone}
        text='Tap "Add to Home Screen"'
      />
    </div>
  );
});

/** Android Firefox/manual menu instructions */
const AndroidMenuContent = memo(function AndroidMenuContent() {
  return (
    <div className="divide-y divide-gray-100 pb-2">
      <InstructionStep
        step={1}
        icon={MoreVertical}
        text="Tap the menu button"
        subtext="⋮ in the address bar"
      />
      <InstructionStep
        step={2}
        icon={Download}
        text='Tap "Install" or "Add to Home Screen"'
      />
    </div>
  );
});

/** In-app browser redirect content */
const InAppBrowserContent = memo(function InAppBrowserContent() {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      triggerHaptic();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  }, []);

  return (
    <div className="space-y-4 pb-4">
      <p className="text-body text-gray-600">
        You're viewing this in an app's browser. To install Postpartner, open this page in Safari or Chrome.
      </p>
      <button
        onClick={handleCopyLink}
        className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                   text-gray-900 font-medium rounded-button motion-safe:transition-colors
                   flex items-center justify-center gap-2"
      >
        {copied ? (
          <>
            <Check className="w-5 h-5 text-green-600" />
            Link Copied!
          </>
        ) : (
          <>
            <Copy className="w-5 h-5" />
            Copy Link
          </>
        )}
      </button>
      <p className="text-label text-gray-500 text-center">
        Then paste in Safari or Chrome to install
      </p>
    </div>
  );
});

/** Get platform-specific content based on install method */
function getSheetContent(
  installMethod: InstallMethod,
  platform: string,
  browser: string,
  isInAppBrowser: boolean,
  onInstall: () => void,
  isInstalling: boolean
) {
  // In-app browser - always show redirect guidance
  if (isInAppBrowser) {
    return <InAppBrowserContent />;
  }

  switch (installMethod) {
    case 'nativePrompt':
      return <NativePromptContent onInstall={onInstall} isInstalling={isInstalling} />;
    case 'manualA2HS':
      // iOS Safari vs iOS Chrome/Edge
      if (platform === 'ios' && browser === 'safari') {
        return <IOSSafariContent />;
      }
      return <IOSChromeContent />;
    case 'manualMenuInstall':
      return <AndroidMenuContent />;
    default:
      return (
        <p className="text-body text-gray-600 pb-4">
          This browser doesn't support app installation. Try opening in Safari or Chrome.
        </p>
      );
  }
}

/** Get appropriate title based on install method */
function getSheetTitle(installMethod: InstallMethod, isInAppBrowser: boolean): string {
  if (isInAppBrowser) return 'Open in Browser';
  if (installMethod === 'nativePrompt') return 'Install Postpartner';
  return 'Add to Home Screen';
}

/** Get appropriate subtitle */
function getSheetSubtitle(installMethod: InstallMethod, isInAppBrowser: boolean): string {
  if (isInAppBrowser) return 'For the best experience';
  if (installMethod === 'nativePrompt') return 'Get the full app experience';
  return 'Quick access from your home screen';
}

export const InstallPostpartnerSheet = memo(function InstallPostpartnerSheet({
  isOpen,
  onClose,
  installState,
}: InstallPostpartnerSheetProps) {
  const [isInstalling, setIsInstalling] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);

  const handleInstall = useCallback(async () => {
    if (!installState.hasNativePrompt) return;

    setIsInstalling(true);
    triggerHaptic();

    try {
      const accepted = await installState.promptInstall();
      if (accepted) {
        setInstallSuccess(true);
        triggerHaptic();
        // Close sheet after brief success animation
        setTimeout(() => {
          onClose();
          setInstallSuccess(false);
        }, 1500);
      }
    } finally {
      setIsInstalling(false);
    }
  }, [installState, onClose]);

  const handleClose = useCallback(() => {
    triggerHaptic();
    onClose();
  }, [onClose]);

  const title = getSheetTitle(installState.installMethod, installState.isInAppBrowser);
  const subtitle = getSheetSubtitle(installState.installMethod, installState.isInAppBrowser);

  // Success state
  if (installSuccess) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={handleClose}
        title="Installed!"
        subtitle="Postpartner is now on your home screen"
        headerIcon={<Check className="w-5 h-5 text-green-600" />}
        headerIconBg="bg-green-50"
      >
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-body text-gray-600 text-center">
            Open Postpartner from your home screen for the best experience.
          </p>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      headerIcon={
        installState.isInAppBrowser
          ? <Link className="w-5 h-5 text-orange-600" />
          : <Download className="w-5 h-5 text-primary-600" />
      }
      headerIconBg={installState.isInAppBrowser ? 'bg-orange-50' : 'bg-primary-50'}
    >
      {getSheetContent(
        installState.installMethod,
        installState.platform,
        installState.browser,
        installState.isInAppBrowser,
        handleInstall,
        isInstalling
      )}
    </BottomSheet>
  );
});

